import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { apiClient } from '../services/api';
import { markPrizeViewed } from '../utils/viewedPrizes';
import { PageHeader } from './PageHeader';
import type { ClaimedPrize } from '../types';
import './PrizePage.css';

function formatPromoCode(code: string): string {
  const digits = code.replace(/\D/g, '');
  if (digits.length <= 4) return code;
  return digits.replace(/(.{4})/g, '$1 ').trim();
}

export const PrizePage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [prize, setPrize] = useState<ClaimedPrize | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const loadPrize = async () => {
      if (!id) {
        setError('Приз не найден');
        setLoading(false);
        return;
      }

      try {
        const numericId = parseInt(id, 10);
        const data = await apiClient.getClaimedPrizeDetail(numericId);
        setPrize(data);
        markPrizeViewed(numericId);
      } catch (err) {
        console.error('Failed to load prize:', err);
        setError('Не удалось загрузить приз');
      } finally {
        setLoading(false);
      }
    };

    loadPrize();
  }, [id]);

  const handleCopy = useCallback(async () => {
    const code = prize?.prize?.promo_code;
    if (!code) return;
    const raw = code.replace(/\s/g, '');
    try {
      await navigator.clipboard.writeText(raw);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Copy failed:', err);
    }
  }, [prize?.prize?.promo_code]);

  if (loading) {
    return (
      <div className="prize-page">
        <div className="prize-page-loading">
          <div className="prize-page-spinner"></div>
        </div>
      </div>
    );
  }

  if (error || !prize) {
    return (
      <div className="prize-page">
        <div className="prize-page-error">
          <p>{error || 'Приз не найден'}</p>
          <button className="prize-page-back-btn" onClick={() => navigate('/my-drops')}>
            <img src="/chevron-back.png" alt="" width={24} height={24} className="prize-page-back-btn-icon" />
            Назад к дропам
          </button>
        </div>
      </div>
    );
  }

  const prizeData = prize.prize;
  const isPhysical = prizeData?.type === 'physical';
  const promoCode = prizeData?.promo_code;

  return (
    <div className="prize-page">
      <PageHeader title="Приз" onBack={() => navigate('/my-drops')} />

      {/* Gradient block with prize name + image */}
      <div className="prize-page-gradient-block">
        <h3 className="prize-page-name">{prizeData?.title || 'Приз'}</h3>
        <div className="prize-page-image-wrap">
          {prizeData?.image_url ? (
            <img
              src={prizeData.image_url}
              alt={prizeData.title}
              className="prize-page-image"
            />
          ) : (
            <div className="prize-page-image-placeholder">🏆</div>
          )}
        </div>
      </div>

      {isPhysical ? (
        <>
          {/* Instructional text for real prize */}
          <p className="prize-page-instruction">
            Чтобы получить приз, свяжитесь с нашей поддержкой — мы поможем всё оформить
          </p>
          {/* Get prize button */}
          <button
            type="button"
            className="prize-page-cta-btn"
            onClick={() => navigate('/support')}
          >
            Получить приз
          </button>
        </>
      ) : (
        <>
          {/* Certificate block for virtual prize */}
          <p className="prize-page-certificate-instruction">
            Сертификат готов к использованию — скопируйте номер
          </p>
          <div className="prize-page-certificate-row">
            <span className="prize-page-certificate-code">
              {promoCode ? formatPromoCode(promoCode) : '—'}
            </span>
            {promoCode && (
              <button
                type="button"
                className="prize-page-copy-btn"
                onClick={handleCopy}
                title="Копировать"
                aria-label="Копировать номер"
              >
                {copied ? (
                  <svg
                    className="prize-page-copy-check"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    aria-hidden
                  >
                    <path
                      d="M5 13l4 4L19 7"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                ) : (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden>
                    <rect x="9" y="9" width="13" height="13" rx="2" stroke="currentColor" strokeWidth="2" fill="none" />
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
};
